/**
 * Testi italiani del piano e del consumo di token (ADR 0023 §4): pagina «Piano»,
 * indicatore nella pagina del set, credenziale di institor nelle impostazioni,
 * consumo nella finestra del piano dell'amministratore.
 *
 * «Token» resta: è l'unità con cui si misura il modello e compare nel piano.
 */

/** Testi del piano e del consumo. */
export const usage = {
  /** Pagina «Piano», in sola lettura. */
  page: {
    /** Titolo della pagina. */
    title: 'Limiti e consumo',
    /** Che cosa mostra. */
    description: 'Il tuo piano, i suoi limiti e i token usati questo mese con la credenziale di institor.',
    /** Lettura non riuscita. */
    loadFailed: 'Lettura del piano non riuscita. Riprova.',
    /** Titolo della sezione dei limiti. */
    limitsTitle: 'Limiti',
    /** Etichetta del piano. */
    plan: 'Piano',
    /** Etichetta del prezzo. */
    price: 'Prezzo',
    /** Etichetta dei set. */
    sets: 'Set di agenti',
    /** Etichetta dei progetti per set. */
    projects: 'Progetti per set',
    /** Etichetta dei token. */
    tokens: 'Token al mese',
    categoryLimit: {
      input: 'Token di input inclusi',
      output: 'Token di output inclusi',
      cache: 'Token di cache inclusi',
    },
    /** Valore di un limite assente. */
    unlimited: 'Senza limite',
    /** Nota per l'amministratore. */
    adminNote: 'Sei amministratore della piattaforma: nessun limite si applica a te.',
    /** Come cambiare piano. */
    changeNote: 'Un piano assegnato da un amministratore resta finché non lo cambia lui.',
    /** Titolo della sezione del consumo. */
    usageTitle: 'Token del mese',
    /** Mese a cui si riferisce il consumo. */
    month: 'Mese {{month}} (UTC). Il conteggio riparte il primo del mese.',
    /** Come si contano i token. */
    rule: 'Contano input e output. I token letti dalla cache contano un decimo.',
    /** Collegamento al grafico per giorno della Panoramica. */
    dailyLink: 'Consumo per giorno',
    /** Titolo della tabella per set. */
    bySet: 'Per set',
    /** Colonna del set. */
    setColumn: 'Set',
    /** Colonna dei token. */
    tokensColumn: 'Token usati',
    /** Set che non esiste più. */
    removedSet: 'Set rimosso',
    /** Nessun consumo. */
    empty: 'Nessun token usato questo mese con la credenziale di institor.',
  },

  /** Indicatore del consumo (pagina del set e pagina «Piano»). */
  meter: {
    /** Usati su limite. */
    usedOfLimit: '{{used}} di {{limit}} token usati',
    /** Usati senza limite. */
    usedUnlimited: '{{used}} token usati, senza limite',
    /** Usati, limite non noto (amministratore sul set di un altro). */
    usedOnly: '{{used}} token usati questo mese',
    /** Rimasti. */
    left: 'Ne restano {{left}}.',
    /** Limite raggiunto. */
    reachedTitle: 'Limite di token raggiunto',
    /** Che cosa fare al limite. */
    reachedBody:
      'I messaggi nuovi sono bloccati fino al primo del mese. Per continuare subito salva la tua credenziale Claude e riavvia il set.',
    /** Nome accessibile della barra. */
    progressLabel: 'Token usati sul limite del mese',
  },

  /** Credenziale di institor nella pagina del set. */
  setCard: {
    /** Titolo del riquadro. */
    title: 'Consumo del mese',
    /** Il relay usa la credenziale di institor. */
    system: 'L’Institore usa la credenziale di institor, inclusa nel piano.',
    /** Il relay usa la credenziale dell'utente. */
    own: 'L’Institore usa la tua credenziale: i token non contano per il limite del piano.',
    /** Nessuna credenziale. */
    none: 'L’Institore non ha una credenziale. Aggiungine una nelle impostazioni.',
    /** Token del set su quelli dell'utente. */
    setShare: 'Questo set: {{used}} token.',
    /** Lettura non riuscita. */
    loadFailed: 'Lettura del consumo non riuscita.',
  },

  /** Stato e azioni della credenziale nelle impostazioni del set. */
  credential: {
    /** Stato: si usa quella di institor. */
    system: 'Credenziale di institor',
    /** Pulsante per mettere la propria al posto di quella di institor. */
    useOwn: 'Usa la tua',
    /** Nome accessibile di «Usa la tua». */
    useOwnNamed: 'Usa la tua credenziale per {{name}}',
    /** Pulsante per tornare a quella di institor. */
    useSystem: 'Usa quella di institor',
    /** Nome accessibile di «Usa quella di institor». */
    useSystemNamed: 'Usa la credenziale di institor per {{name}}',
    /** Domanda della conferma. */
    useSystemQuestion: 'Tornare alla credenziale di institor?',
    /** Conseguenza della conferma. */
    useSystemConsequence:
      'La tua credenziale di {{name}} viene cancellata. Dal prossimo avvio il set usa quella di institor e i token contano per il limite del piano.',
    /** Esito. */
    useSystemDone: 'Credenziale tolta. Riavvia il set per usare quella di institor.',
    /** Errore. */
    useSystemFailed: 'Operazione non riuscita. Riprova.',
    /** Nota sotto la tabella. */
    restartNote: 'Un cambio di credenziale vale dal prossimo avvio del set.',
  },

  /** Consumo nella finestra del piano dell'amministratore. */
  admin: {
    /** Titolo della sezione. */
    title: 'Token del mese',
    /** Usati su limite. */
    usedOfLimit: '{{used}} di {{limit}} token usati con la credenziale di institor ({{month}}).',
    /** Usati senza limite. */
    usedUnlimited: '{{used}} token usati con la credenziale di institor ({{month}}), senza limite.',
    /** Lettura non riuscita. */
    loadFailed: 'Lettura del consumo non riuscita.',
  },
};

/** Forma dei testi del piano e del consumo. */
export type Usage = typeof usage;
